import { sha256 } from "@noble/hashes/sha2.js";
import { ApiError } from "@/lib/api/error";
import type { AbortIn, CompleteIn, CompleteOut, EntityHint, PresignIn, PresignOut } from "@/lib/api/model";
import type { MeetingType } from "./labels";

export type UploadPhase = "hashing" | "probing" | "presigning" | "uploading" | "completing" | "done" | "aborted" | "failed";
export interface UploadProgress { phase: UploadPhase; bytesSent: number; bytesTotal: number; partsDone: number; partsTotal: number }
export interface UploadMeta { startedAt: string; title?: string; entityHint?: EntityHint | null; projectId?: string | null; meetingType?: MeetingType | null }

export interface UploadDeps {
  presign(body: PresignIn): Promise<PresignOut>;
  complete(body: CompleteIn): Promise<CompleteOut>;
  abort(body: AbortIn): Promise<void>;
  hash?(file: File): Promise<string>;
  probeDuration?(file: File): Promise<number>;
  createXhr?(): XMLHttpRequest;
  sleep?(ms: number): Promise<void>;
}

export type UploadErrorCode = "UNSUPPORTED_TYPE" | "DURATION_UNREADABLE" | "UPLOAD_TOO_LARGE" | "UPLOAD_TOO_SHORT" | "UPLOAD_TOO_LONG" | "ETAG_MISSING" | "PART_FAILED" | "NETWORK";
export class UploadError extends Error {
  constructor(public code: UploadErrorCode, message: string = code) {
    super(message);
    this.name = "UploadError";
  }
}

export const PART_RETRIES = 3;
export const PART_BACKOFF_MS = 500;
export const COMPLETE_RETRY_DELAY_MS = 2_000;
const SUBTLE_MAX_BYTES = 64 * 1024 * 1024;   // one ArrayBuffer up to 64 MiB; @noble/hashes streams above
export const MIN_DURATION_S = 60;             // CONV_UPLOAD_MIN_DURATION_S
export const MAX_DURATION_S = 18_000;         // CONV_UPLOAD_MAX_DURATION_S
export const MAX_UPLOAD_BYTES = 4 * 1024 ** 3; // CONV_UPLOAD_MAX_BYTES
const HASH_CHUNK_BYTES = 16 * 1024 * 1024;

/**
 * The requirements' `.wav .mp3 .mp4 .mpeg .mov` plus the in-browser recorder's `.webm` / `.m4a` / `.ogg`, resolved from
 * the extension (browsers leave `file.type` empty for .mov/.wav).
 */
const CONTENT_TYPE_BY_EXT: Record<string, string> = {
  wav: "audio/wav", mp3: "audio/mpeg", mp4: "video/mp4", mpeg: "video/mpeg", mpg: "video/mpeg", mov: "video/quicktime",
  webm: "audio/webm", m4a: "audio/mp4", ogg: "audio/ogg",
};
export const ACCEPTED_EXTENSIONS = ".wav,.mp3,.mp4,.mpeg,.mpg,.mov,.webm,.m4a,.ogg";

export function contentTypeFor(file: File): string | null {
  const ext = file.name.toLowerCase().split(".").pop() ?? "";
  return CONTENT_TYPE_BY_EXT[ext] ?? null;
}

function hex(bytes: Uint8Array): string {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * SHA-256 hex. SubtleCrypto has no streaming API, so ≤ 64 MiB is hashed from one ArrayBuffer; above that
 * @noble/hashes (pure JS — a WebAssembly hasher is refused by the production CSP) streams 16 MiB slices.
 */
export async function hashFile(file: File): Promise<string> {
  if (file.size <= SUBTLE_MAX_BYTES) {
    return hex(new Uint8Array(await crypto.subtle.digest("SHA-256", await file.arrayBuffer())));
  }
  const h = sha256.create();
  for (let off = 0; off < file.size; off += HASH_CHUNK_BYTES) {
    h.update(new Uint8Array(await file.slice(off, off + HASH_CHUNK_BYTES).arrayBuffer()));
  }
  return hex(h.digest());
}

/**
 * Integer seconds from a hidden media element's `loadedmetadata` (a `<video>` element decodes audio files too).
 * A MediaRecorder WebM carries no duration header, so the element reports `Infinity`; seeking past the end makes the
 * browser scan to the last cluster and fire `durationchange` with the real length. A seek that ends still unknown is unreadable.
 */
export function probeDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const el = document.createElement("video");
    el.preload = "metadata";
    let settled = false;
    const settle = (d: number) => {
      if (settled) return;
      settled = true;
      URL.revokeObjectURL(url);
      el.removeAttribute("src");
      el.load();
      if (!Number.isFinite(d) || d <= 0) reject(new UploadError("DURATION_UNREADABLE"));
      else resolve(Math.round(d));
    };
    el.onloadedmetadata = () => {
      if (el.duration !== Infinity) { settle(el.duration); return; }
      el.ondurationchange = () => { if (Number.isFinite(el.duration)) settle(el.duration); };
      el.onseeked = () => settle(el.duration);
      el.currentTime = Number.MAX_SAFE_INTEGER;
    };
    el.onerror = () => settle(Number.NaN);
    el.src = url;
  });
}

const defaultSleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

function abortError(): DOMException {
  return new DOMException("Upload aborted", "AbortError");
}

/** One presigned PUT. Resolves the ETag (quotes stripped). Rejects UploadError("NETWORK") / ("ETAG_MISSING") or AbortError. */
function putPart(xhr: XMLHttpRequest, url: string, blob: Blob, onLoaded: (loaded: number) => void, signal: AbortSignal): Promise<string> {
  return new Promise((resolve, reject) => {
    xhr.open("PUT", url);
    xhr.upload.onprogress = (evt) => { if (evt.lengthComputable) onLoaded(evt.loaded); };
    xhr.onload = () => {
      if (xhr.status < 200 || xhr.status >= 300) { reject(new UploadError("NETWORK", `part status ${xhr.status}`)); return; }
      const etag = xhr.getResponseHeader("ETag");
      if (!etag) { reject(new UploadError("ETAG_MISSING")); return; }
      onLoaded(blob.size);
      resolve(etag.replace(/^"|"$/g, ""));
    };
    xhr.onerror = () => reject(new UploadError("NETWORK"));
    xhr.onabort = () => reject(abortError());
    if (signal.aborted) { reject(abortError()); return; }
    const onAbort = () => xhr.abort();
    signal.addEventListener("abort", onAbort, { once: true });
    xhr.onloadend = () => signal.removeEventListener("abort", onAbort);   // 256 parts must not leave 256 live listeners on one signal
    xhr.send(blob);
  });
}

export async function uploadFile(
  { file, meta, onProgress, signal }: { file: File; meta: UploadMeta; onProgress: (p: UploadProgress) => void; signal: AbortSignal },
  deps: UploadDeps,
): Promise<CompleteOut> {
  const hash = deps.hash ?? hashFile;
  const probe = deps.probeDuration ?? probeDuration;
  const createXhr = deps.createXhr ?? (() => new XMLHttpRequest());
  const sleep = deps.sleep ?? defaultSleep;

  const contentType = contentTypeFor(file);
  if (!contentType) throw new UploadError("UNSUPPORTED_TYPE");

  const progress: UploadProgress = { phase: "hashing", bytesSent: 0, bytesTotal: file.size, partsDone: 0, partsTotal: 0 };
  const report = (patch: Partial<UploadProgress>) => { Object.assign(progress, patch); onProgress({ ...progress }); };
  const throwIfAborted = () => { if (signal.aborted) throw abortError(); };

  if (file.size > MAX_UPLOAD_BYTES) throw new UploadError("UPLOAD_TOO_LARGE");   // before reading the file twice (hash + probe)
  report({ phase: "hashing" });
  const contentHash = await hash(file);
  throwIfAborted();
  report({ phase: "probing" });
  const durationS = await probe(file);
  throwIfAborted();
  // Client-side mirror of A's complete-time bounds (§3.1 / §3.2). Failing here costs nothing; failing at `complete`
  // would already have burned the org's never-released `daily_upload_bytes` reservation and a presign count (§10.19).
  if (durationS < MIN_DURATION_S) throw new UploadError("UPLOAD_TOO_SHORT");
  if (durationS > MAX_DURATION_S) throw new UploadError("UPLOAD_TOO_LONG");

  report({ phase: "presigning" });
  const presigned = await deps.presign({ bytes: file.size, content_type: contentType, filename: file.name });
  const token = presigned.upload_token;
  const parts = [...presigned.part_urls].sort((a, b) => a.part_number - b.part_number);
  report({ phase: "uploading", partsTotal: parts.length });

  const abortRemote = async () => {
    // no phase report here — the abort branch reports "aborted", the part-failure branch reports "failed"
    try { await deps.abort({ upload_token: token }); } catch { /* the 24 h lifecycle rule reclaims the parts */ }
  };

  const etags: { part_number: number; etag: string }[] = [];
  let doneBytes = 0;
  try {
    for (const part of parts) {
      const start = (part.part_number - 1) * presigned.part_size;
      const blob = file.slice(start, Math.min(start + presigned.part_size, file.size));
      let etag: string | null = null;
      for (let attempt = 1; attempt <= PART_RETRIES; attempt++) {
        throwIfAborted();
        try {
          // Math.max: a retried part restarts its own XHR progress at 0; the bar must not fall back after a network blip.
          etag = await putPart(createXhr(), part.url, blob, (loaded) => report({ bytesSent: Math.max(progress.bytesSent, doneBytes + loaded) }), signal);
          break;
        } catch (err) {
          if ((err as Error).name === "AbortError") throw err;
          if (err instanceof UploadError && err.code === "ETAG_MISSING") throw err; // a config problem: retrying cannot fix it
          if (attempt === PART_RETRIES) throw new UploadError("PART_FAILED");
          await sleep(PART_BACKOFF_MS * 2 ** (attempt - 1));
        }
      }
      etags.push({ part_number: part.part_number, etag: etag as string });
      doneBytes += blob.size;
      report({ bytesSent: doneBytes, partsDone: etags.length });
    }
  } catch (err) {
    await abortRemote();
    report({ phase: (err as Error).name === "AbortError" ? "aborted" : "failed" });   // a user Cancel shows "Cancelled", never "Upload failed"
    throw err;
  }

  throwIfAborted();
  report({ phase: "completing" });
  const body: CompleteIn = {
    upload_token: token,
    parts: etags,
    content_hash: contentHash,
    duration_s: durationS,
    started_at: meta.startedAt,
    ...(meta.entityHint ? { entity_hint: meta.entityHint } : {}),
    ...(meta.projectId ? { project_id: meta.projectId } : {}),
    ...(meta.title ? { title: meta.title } : {}),
    ...(meta.meetingType ? { meeting_type: meta.meetingType } : {}),
  };
  try {
    const out = await deps.complete(body);
    report({ phase: "done" });
    return out;
  } catch (err) {
    if (err instanceof ApiError && err.status === 503 && err.code === "PROCESSING_UNAVAILABLE") {
      await sleep(COMPLETE_RETRY_DELAY_MS);
      const out = await deps.complete(body); // same one-shot token: the server left the jti `complete-retryable` (spec 03 §3.0)
      report({ phase: "done" });
      return out;
    }
    report({ phase: "failed" });
    throw err;
  }
}
